import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StatusMappingEditor } from "@/components/itsm-config/mapping-editors";
import { ScopeReveal } from "@/components/itsm-config/scope-reveal";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import { useExternalStatusOptions } from "@/hooks/use-itsm-metadata";
import { findRule } from "@/lib/itsm-config/general-mapping";
import {
  HARMONY_STATUSES,
  HARMONY_STATUS_LABELS,
  PROVIDER_LABELS,
  type GeneralMappingRule,
  type ProviderConfig,
  type ItsmProviderFamily,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

/** Card 3 — Harmony status → one or more external statuses. */
export function StatusMappingSection({
  tenantId,
  credentialId,
  provider,
  family,
  providerConfig,
}: Props) {
  const rules = providerConfig.general_mapping ?? [];
  const save = useGeneralMappingSave();
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;
  const externalOptions = useExternalStatusOptions(
    family,
    tenantId,
    credentialId,
  );

  const hasField = (r: GeneralMappingRule) =>
    Boolean(r.status_mapping && Object.keys(r.status_mapping).length);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status translation</CardTitle>
        <CardDescription>
          Map each Harmony status to one or more {providerLabel} statuses.
          {family === "jira" && (
            <>
              {" "}
              Jira workflows often have several states that mean the same thing
              — map one Harmony status to all of them. Used both ways: outbound,
              Harmony uses the first reachable one; inbound, any of them imports
              as that Harmony status.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScopeReveal
          family={family}
          tenantId={tenantId}
          credentialId={credentialId}
          rules={rules}
          hasField={hasField}
          onClearScope={(match) => { save(match, { status_mapping: null }); }}
          renderEditor={(match) => (
            <StatusMappingEditor
              value={findRule(rules, match)?.status_mapping ?? {}}
              onChange={(m) => {
                save(match, {
                  status_mapping: Object.keys(m).length ? m : null,
                });
              }}
              harmonyOptions={HARMONY_STATUSES}
              harmonyLabels={HARMONY_STATUS_LABELS}
              providerLabel={providerLabel}
              externalOptions={externalOptions}
              bidirectional={family === "jira"}
            />
          )}
        />
      </CardContent>
    </Card>
  );
}
